import { Building2, Calculator, FileText, Globe, Phone, RefreshCw, Users, Wrench } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const TRADE = [
    { icon: FileText, de: "Zugang zu Händler-Preislisten & Gesamtkatalog", en: "Access to trade price lists & the full catalogue" },
    { icon: Calculator, de: "Projektbezogene Pegelberechnung & Auslegung", en: "Project-specific level calculation & design" },
    { icon: Wrench, de: "48h Reparaturabwicklung für Kundenaufträge", en: "48h repair handling for customer orders" },
    { icon: Phone, de: "Direkter Draht zu Technikern & Entwicklern", en: "A direct line to technicians & developers" },
];

const END = [
    { icon: Building2, de: "Schlüsselfertige Kopfstationen für Wohnanlagen", en: "Turnkey headends for residential complexes" },
    { icon: Globe, de: "Einspeisung ausländischer TV-Programme & Info-Kanäle", en: "Feed-in of foreign TV channels & info channels" },
    { icon: RefreshCw, de: "Modernisierung alter Kabel- & GA-Anlagen", en: "Upgrading old cable & communal systems" },
    { icon: Users, de: "Vermittlung qualifizierter Partner-Installateure", en: "Referrals to qualified partner installers" },
];

export default function TargetSplit() {
    const { t } = useLang();
    return (
        <section id="zielgruppen" data-testid="zielgruppen" className="scroll-mt-24 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <Reveal>
                    <Eyebrow>{t("Zielgruppen", "Target groups")}</Eyebrow>
                    <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
                        {t("Ein Partner. Zwei Zielgruppen.", "One partner. Two target groups.")}
                    </h2>
                </Reveal>
                <div className="mt-10 grid md:grid-cols-2 gap-5">
                    <Reveal>
                        <div className="h-full rounded-2xl bg-white border border-slate-200 border-t-4 border-t-red-700 p-7 sm:p-9 shadow-sm hover:shadow-md transition-shadow" data-testid="card-fachhandel">
                            <Eyebrow>{t("Für Fachhandel & Installateure", "For trade & installers")}</Eyebrow>
                            <ul className="mt-6 space-y-4">
                                {TRADE.map(({ icon: Icon, de, en }) => (
                                    <li key={de} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                                        <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-red-50 border border-red-100 text-red-700 shrink-0">
                                            <Icon className="h-4 w-4" />
                                        </span>
                                        <span className="pt-1">{t(de, en)}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <a
                                    href="tel:+49919594040"
                                    data-testid="fachhandel-phone-cta"
                                    className="inline-flex items-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white font-semibold text-sm px-6 py-3 transition-colors"
                                >
                                    <Phone className="h-4 w-4" />
                                    {t("Telefonische Fachberatung", "Expert advice by phone")}
                                </a>
                                <a
                                    href="/datenblaetter/katalog_ohne_preise.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="fachhandel-katalog-link"
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:border-red-400 hover:text-red-700 text-slate-700 font-semibold text-sm px-6 py-3 transition-colors"
                                >
                                    <FileText className="h-4 w-4" />
                                    {t("Fachhandelskatalog (PDF)", "Trade catalogue (PDF)")}
                                </a>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="h-full rounded-2xl bg-white border border-slate-200 border-t-4 border-t-emerald-600 p-7 sm:p-9 shadow-sm hover:shadow-md transition-shadow" data-testid="card-endkunden">
                            <Eyebrow>{t("Für Endkunden, Hotels & Hausverwaltungen", "For end customers, hotels & property managers")}</Eyebrow>
                            <ul className="mt-6 space-y-4">
                                {END.map(({ icon: Icon, de, en }) => (
                                    <li key={de} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                                        <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-700 shrink-0">
                                            <Icon className="h-4 w-4" />
                                        </span>
                                        <span className="pt-1">{t(de, en)}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8">
                                <a
                                    href="mailto:vertrieb@satmueller.de?subject=Beratungsanfrage%20SAT-%20%26%20Kabelanlage"
                                    data-testid="endkunden-mail-cta"
                                    className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-6 py-3 transition-colors"
                                >
                                    {t("E-Mail Anfrage", "Email enquiry")}: vertrieb@satmueller.de
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
